import type { ComponentPropsWithoutRef } from 'react';

import Icon from 'helpers/svg-icon';

import { grey70, cefRed, white } from 'constants/variables';
import { iconGrey40 } from 'constants/shared_icon_exports';

export type IconProps = Omit<
  ComponentPropsWithoutRef<'svg'>,
  'version' | 'xmlns' | 'xmlnsXlink'
> & {
  fillColor?: string;
  isVisible?: boolean;
};

const DocumentIcon = ({
  fillColor = grey70,
  width = 15,
  height = 19,
  ...rest
}: IconProps) => (
  <Icon
    viewBox="0 0 15 19"
    x="0px"
    y="0px"
    width={width}
    height={height}
    {...rest}>
    <path
      d="M15.612,8.98V4H5.591V22.725h15V8.98Zm2.353,6.6H8.217V14.012h9.748Zm0-3.134H8.217V10.89h9.748Z"
      transform="translate(-5.591 -4.001)"
      fill={fillColor}
    />
    <path
      d="M15.04,4V7.94h3.938Z"
      transform="translate(-3.983 -4.001)"
      fill={fillColor}
    />
  </Icon>
);

const UserIcon = ({
  fillColor = iconGrey40,
  width = 34,
  height = 34,
  ...rest
}: IconProps) => (
  <Icon height={height} width={width} viewBox="0 0 99.21 87.01" {...rest}>
    <path
      fill={fillColor}
      d="M69.78 20S68.48 8.86 57.23 5c0 0-2-5-4.61-5h-4.94c-2.58 0-4.61 5-4.61 5-11.25 3.86-12.55 15-12.55 15-5 0-3.69 6.92-3.69 6.92h46.64S74.76 20 69.78 20zM73.22 54.8H60.41L49.8 76.28 39.2 54.8H26.39C7 54.8 7 87 7 87h85.59s0-32.2-19.37-32.2zM31.64 31.19v.09c0 13 8.18 23.52 18.26 23.52s18.26-10.53 18.26-23.52v-.09z"
    />
  </Icon>
);

const StoreIcon = ({
  width = 30,
  height = 20,
  fillColor,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 40 40" {...rest}>
    <path
      fill={fillColor}
      d="M3.4,11v3.1h2.4v16.9h28.3V14.1h2.5V11C19.9,6.7,3.4,11,3.4,11z M19.6,29.9h-4.7v-9.3h4.7V29.9z M25.1,29.9
  h-4.7v-9.3h4.7L25.1,29.9z"
    />
  </Icon>
);

const PhoneIcon = ({
  width = 18,
  height = 18,
  fillColor,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 53 74.71" {...rest}>
    <path
      fill={fillColor}
      d="M8.44,46.48C6.15,42.16,1.72,28.1,1.72,28.1S-2.59,6.6,15,.42l8.41,2.26,3.12,15.67-10.9,6.76S13.9,30.07,19,42h0c5.89,11.63,10.79,13.49,10.79,13.49l12.18-4,10,12.43-3.65,7.91c-16,9.53-29.75-7.55-29.75-7.55S11,51.37,8.44,46.48Z"
    />
  </Icon>
);

const EmailIcon = ({
  width = 13,
  height = 8,
  fillColor,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 69.56 52.38" {...rest}>
    <polygon
      fill={fillColor}
      points="68.46 0.69 1.1 0.69 34.78 32.33 68.46 0.69"
    />
    <polygon
      fill={fillColor}
      points="39.57 37.43 34.78 41.93 29.98 37.43 1.1 10.29 1.1 51.38 68.46 51.38 68.46 10.29 39.57 37.43"
    />
  </Icon>
);

const VanIcon = ({
  width = 30,
  height = 20,
  fillColor,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 64 64" {...rest}>
    <g>
      <path
        fill={fillColor}
        d="M59.4855,25.4556l-1.6934-5.2174a2.7018,2.7018,0,0,1-.15-.27c-.8453-1.7471-1.9246-2.1265-2.5076-2.1951H24.62a6.2359,6.2359,0,0,0-2.83,1.3008l-.0029-.0064L11.7089,29.0733,5.1282,33.091A3.6084,3.6084,0,0,0,4,34.6107l.0522,2.9716a8.6684,8.6684,0,0,0,.6385,1.87l.3888.6415c.6637.8474,3.4588.8495,3.4588.8495l.0334.1335a6.4759,6.4759,0,0,1,12.9416.0291H43.3666a6.4757,6.4757,0,0,1,12.9424,0h2.0457A1.68,1.68,0,0,0,60,39.9429V27.5243A14.9341,14.9341,0,0,0,59.4855,25.4556ZM28.5376,29.48H14.473l7.5608-8.0486H28.7Z"
      />
      <circle fill={fillColor} cx="15.0422" cy="41.3493" r="4.8779" />
      <circle fill={fillColor} cx="49.8377" cy="41.3493" r="4.8779" />
    </g>
  </Icon>
);

const CarIcon = ({
  width = 32,
  height = 32,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 32 32" {...rest}>
    <path
      fill={fillColor}
      d="M27.84 11.51h.76c.48 0 .88-.39.88-.88s-.39-.88-.88-.88h-1.58l-2.07-4.42a.859.859 0 0 0-.79-.5H7.84c-.34 0-.65.2-.79.5L4.98 9.75H3.4c-.48 0-.88.39-.88.88s.39.88.88.88h.76l-1.55 3.31c-.05.12-.08.24-.08.37v9.46a2.518 2.518 0 0 0 .74 1.78c.23.23.51.42.82.55.31.13.63.19.95.19h2.09c.31 0 .66-.06.97-.19.31-.13.58-.31.82-.55a2.518 2.518 0 0 0 .74-1.78v-1.33h12.7v1.33c0 .33.06.66.19.96.13.31.31.58.54.82a2.518 2.518 0 0 0 1.79.74h2.09a2.518 2.518 0 0 0 2.51-2.52v-9.46c0-.13-.03-.25-.08-.37l-1.55-3.31ZM8.4 6.58h15.21l2.05 4.37s0 .02.01.03l2.06 4.39v6.19H4.28v-6.18l4.12-8.8Zm-.5 18.07c0 .1-.02.2-.06.29-.04.09-.09.18-.17.25-.07.07-.16.13-.25.17-.09.04-.19.06-.29.06H5.04c-.1 0-.2-.02-.29-.06a.852.852 0 0 1-.25-.17.852.852 0 0 1-.17-.25c-.04-.1-.06-.2-.06-.3v-1.33h3.62v1.33Zm19.82 0a.803.803 0 0 1-.22.55c-.07.07-.16.13-.25.17-.09.04-.19.06-.29.06h-2.09c-.1 0-.2-.02-.3-.06a.696.696 0 0 1-.25-.17.852.852 0 0 1-.17-.25.717.717 0 0 1-.06-.29v-1.33h3.62v1.33Z"
    />
    <path
      fill={fillColor}
      d="M18.71 16.99h-5.43c-.48 0-.88.39-.88.88s.39.88.88.88h5.43c.48 0 .88-.39.88-.88s-.39-.88-.88-.88ZM23.74 16.45c-.21 0-.41.07-.57.21l-1.26 1.07a.877.877 0 0 0 .57 1.54h2.33c.48 0 .88-.39.88-.88v-1.07c0-.48-.39-.88-.88-.88h-1.07ZM7.18 19.27h2.33a.877.877 0 0 0 .57-1.54l-1.25-1.07a.855.855 0 0 0-.57-.21H7.18c-.48 0-.88.39-.88.88v1.07c0 .48.39.88.88.88ZM7.81 14.64h16.37c.3 0 .59-.16.75-.42.16-.26.17-.58.04-.85L22.46 8.4a.883.883 0 0 0-.78-.48H10.33c-.33 0-.63.19-.78.48l-2.51 4.97c-.14.27-.12.59.04.85s.44.42.75.42Zm3.04-4.97h10.28l1.63 3.22H9.23l1.62-3.22Z"
    />
  </Icon>
);

const TrainIcon = ({
  width = 32,
  height = 32,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 32 32" {...rest}>
    <path
      fill={fillColor}
      d="M16 2.5c-3.1 0-10.3.3-10.3 3.11v16.23c0 .84.33 1.64.92 2.23.49.48 1.1.78 1.77.88l-1.35 3.34c-.18.45.04.96.48 1.14.11.04.22.06.33.06.35 0 .67-.21.81-.55l.28-.71h14.1l.29.71c.14.34.46.55.81.55.11 0 .22-.02.33-.06.45-.18.66-.69.48-1.14l-.51-1.26v-.01l-.84-2.07c.67-.1 1.29-.39 1.78-.88.6-.6.92-1.39.92-2.23V5.61c0-2.8-7.2-3.11-10.3-3.11ZM9.66 26.49l.6-1.49h11.47l.61 1.49H9.66Zm14.89-4.65c0 .38-.15.73-.41.99-.26.27-.62.41-.99.41H8.87c-.38 0-.73-.15-.99-.41-.27-.26-.41-.62-.41-.99V5.68c.26-.5 3.05-1.42 8.55-1.42s8.29.93 8.55 1.36v16.23Z"
    />
    <path
      fill={fillColor}
      d="M10.71 16.79h10.56c1.06 0 1.93-.86 1.93-1.93V8.03c0-1.06-.86-1.93-1.93-1.93H10.71c-1.06 0-1.92.86-1.92 1.93v6.83c0 1.06.86 1.93 1.92 1.93Zm-.17-8.75c0-.1.08-.18.17-.18h10.56c.1 0 .18.08.18.18v6.83c0 .1-.08.18-.18.18H10.71c-.1 0-.17-.08-.17-.18V8.04ZM17.97 18.72h-3.94c-.48 0-.88.39-.88.88s.39.88.88.88h3.94c.48 0 .88-.39.88-.88s-.39-.88-.88-.88ZM10.25 18.25c-.81 0-1.47.66-1.47 1.47s.66 1.47 1.47 1.47 1.47-.66 1.47-1.47-.66-1.47-1.47-1.47Zm0 1.75c-.16 0-.28-.13-.28-.28s.13-.28.28-.28.28.13.28.28-.13.28-.28.28ZM21.75 18.25c-.81 0-1.47.66-1.47 1.47s.66 1.47 1.47 1.47 1.47-.66 1.47-1.47-.66-1.47-1.47-1.47Zm0 1.75c-.16 0-.28-.13-.28-.28s.13-.28.28-.28.28.13.28.28-.13.28-.28.28Z"
    />
  </Icon>
);

const CalendarIcon = ({
  width = 18,
  height = 20,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 18 20" {...rest}>
    <path
      fill={fillColor}
      d="M14 11H9v5h5v-5ZM13 0v2H5V0H3v2H.01L0 20h18V2h-3V0h-2Zm3 18H2V7h14v11Z"
    />
  </Icon>
);

const PersonIcon = ({
  width = 16,
  height = 16,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 16 16" {...rest}>
    <path
      fill={fillColor}
      d="M8 8c-1.1 0-2.042-.392-2.825-1.175C4.392 6.042 4 5.1 4 4s.392-2.042 1.175-2.825C5.958.392 6.9 0 8 0s2.042.392 2.825 1.175C11.608 1.958 12 2.9 12 4s-.392 2.042-1.175 2.825C10.042 7.608 9.1 8 8 8Zm-8 8v-2.8c0-.567.146-1.087.438-1.562A2.93 2.93 0 0 1 1.6 10.55a14.824 14.824 0 0 1 3.15-1.162 13.812 13.812 0 0 1 6.5 0c1.067.259 2.117.646 3.15 1.162.483.25.871.613 1.163 1.088.292.475.438.996.437 1.562V16H0Zm2-2h12v-.8a.966.966 0 0 0-.5-.85c-.9-.45-1.808-.787-2.725-1.012a11.586 11.586 0 0 0-5.55 0A13.09 13.09 0 0 0 2.5 12.35a.977.977 0 0 0-.363.35.93.93 0 0 0-.137.5v.8Zm6-8c.55 0 1.021-.196 1.413-.587A1.92 1.92 0 0 0 10 4c0-.55-.196-1.02-.587-1.412A1.93 1.93 0 0 0 8 2c-.55 0-1.02.196-1.412.588A1.93 1.93 0 0 0 6 4c0 .55.196 1.021.588 1.413.392.392.863.588 1.412.587Z"
    />
  </Icon>
);

const TicketIcon = ({
  width = 20,
  height = 16,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 20 16" {...rest}>
    <path fill={fillColor} d="M12 7v3h2V6h-2v1Z" />
    <path
      fill={fillColor}
      d="M20 6V0H0v6c1.1 0 2 .9 2 2s-.9 2-2 2v6h20v-6c-1.1 0-2-.9-2-2s.9-2 2-2Zm-2-1.46c-1.19.69-2 1.99-2 3.46s.81 2.77 2 3.46V14h-4v-2h-2v2H2v-2.54C3.19 10.77 4 9.47 4 8s-.81-2.77-2-3.46V2h10v2h2V2h4v2.54Z"
    />
  </Icon>
);

const LocationPinIcon = ({
  width = 12,
  height = 12,
  fillColor = grey70,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 12.915 18" {...rest}>
    <path
      d="M12.915,6.457A6.457,6.457,0,1,0,0,6.457,6.351,6.351,0,0,0,1.15,10.13h0L6.457,18l5.307-7.87h0A6.351,6.351,0,0,0,12.915,6.457ZM6.457,9.666A3.209,3.209,0,1,1,9.666,6.457,3.2,3.2,0,0,1,6.457,9.666Z"
      fill={fillColor}
    />
  </Icon>
);

const LocationPinOutlinedIcon = ({
  width = 12,
  height = 12,
  fillColor = cefRed,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} viewBox="0 0 24 24" {...rest}>
    <path
      fill={fillColor}
      d="M12 6.5c.66 0 1.3.26 1.77.73s.73 1.1.73 1.77c0 .33-.06.65-.19.96-.13.3-.31.58-.54.81-.23.23-.51.42-.81.54s-.63.19-.96.19c-.66 0-1.3-.26-1.77-.73S9.5 9.67 9.5 9s.26-1.3.73-1.77 1.1-.73 1.77-.73ZM12 2a7.001 7.001 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7.001 7.001 0 0 1 7-7Zm0 2c-1.33 0-2.6.53-3.54 1.46C7.52 6.4 7 7.67 7 9c0 1 0 3 5 9.71C17 12 17 10 17 9c0-1.33-.53-2.6-1.46-3.54A4.994 4.994 0 0 0 12 4Z"
    />
  </Icon>
);

const PriceDrop = ({
  width = '15.25',
  height = '14',
  fillColor = white,
  ...rest
}: IconProps) => (
  <Icon width={width} height={height} data-testid="price-drop-icon" {...rest}>
    <g
      fill={fillColor}
      clipPath="url(#a)"
      data-name="Group 19723"
      transform="translate(-.595)">
      <path
        d="m15.02 7.653-1.21 1.21V2.989h-1.242v5.874l-1.25-1.242-.785.874 2.58 2.572.08.072 2.653-2.669Z"
        data-name="Path 11594"
      />
      <path
        d="M11.334 11.211a5.639 5.639 0 1 1-.272-8.647l.97-.97a7 7 0 1 0 .256 10.57l-.962-.962Z"
        data-name="Path 11595"
      />
      <path
        d="M5.388 8.647h.617V7.461h-.474v-.906h.473v-.858a1.631 1.631 0 0 1 1.844-1.61 1.524 1.524 0 0 1 1.675 1.475v.329H8.361v-.24a.513.513 0 0 0-.553-.537.506.506 0 0 0-.529.537v.906h1.362v.906H7.279v1.184h2.352l-.224 1.034H5.388Z"
        data-name="Path 11596"
      />
    </g>
  </Icon>
);

const Clearance = ({
  width = '15.25',
  height = '14',
  fillColor = white,
  ...rest
}: IconProps) => (
  <Icon
    width={width}
    height={height}
    viewBox="0 0 32 32"
    fill={fillColor}
    data-testid="clearance-icon"
    {...rest}>
    <g>
      <path d="M20.09 16.984c-.576 0-.848.305-.848.737v2.705c0 .448.24.769.848.769s.848-.385.848-.785v-2.69c0-.4-.272-.736-.848-.736Zm-7.38-2.753v-2.69c0-.4-.288-.752-.864-.752-.576 0-.849.337-.849.753v2.705c0 .416.24.752.849.752.608 0 .864-.368.864-.768Z" />
      <path d="m31.984 16.008-3.346-3.394 1.217-4.61-4.61-1.249-1.249-4.61-4.61 1.217L15.992 0l-3.394 3.346-4.61-1.217-1.249 4.61-4.61 1.249 1.217 4.61L0 15.992l3.346 3.394-1.217 4.61 4.61 1.249 1.249 4.61 4.61-1.217L15.992 32l3.394-3.362 4.61 1.217 1.249-4.61 4.61-1.249-1.217-4.61L32 15.992l-.016.016Zm-22.988-1.76v-2.61c0-1.729 1.441-2.385 2.834-2.385s2.881.64 2.881 2.433v2.577c0 1.649-1.52 2.337-2.85 2.337-1.328 0-2.865-.608-2.865-2.353Zm3.586 8.323H10.87l8.629-13.174h1.713L12.582 22.57Zm10.373-2.16c0 1.664-1.52 2.352-2.865 2.352-1.345 0-2.85-.624-2.85-2.353V17.8c0-1.728 1.441-2.368 2.834-2.368s2.881.624 2.881 2.4v2.578Z" />
    </g>
  </Icon>
);

const RedemptionOffer = ({
  width = '16',
  height = '16',
  fillColor = white,
  ...rest
}: IconProps) => (
  <Icon
    width={width}
    height={height}
    viewBox="0 0 16 16"
    fill={fillColor}
    {...rest}>
    <path d="M-0.00634766 2.4126V6.11084L0.515137 6.15088C1.4751 6.22314 2.22705 7.03564 2.22705 7.99951C2.22705 8.96338 1.4751 9.77588 0.515137 9.84912L-0.00634766 9.88916V13.5874H16.0063V2.4126H-0.00634766ZM14.8774 12.4585H5.95947V11.1392H4.83057V12.4585H1.12256V10.8872C2.40967 10.5503 3.35596 9.36963 3.35596 7.99951C3.35596 6.62842 2.40967 5.44873 1.12256 5.11279V3.5415H4.83057V4.61963H5.95947V3.5415H14.8774V12.4585Z" />
    <path d="M8.99127 8.75586L8.60962 10.7785L10.4153 9.79047L12.221 10.7785L11.8394 8.75586L13.337 7.34381L11.2954 7.08179L10.4153 5.22113L9.53516 7.08179L7.49359 7.34381L8.99127 8.75586Z" />
    <path d="M5.95947 6.48193H4.83057V9.27588H5.95947V6.48193Z" />
  </Icon>
);

interface PasswordVisibilityIconProps extends IconProps {
  isVisible?: boolean;
}

const PasswordVisibilityIcon = ({
  width = 24,
  height = 24,
  fillColor = grey70,
  isVisible = false,
  ...rest
}: PasswordVisibilityIconProps) => (
  <Icon width={width} height={height} fill="none" viewBox="0 0 24 24" {...rest}>
    <path
      fill={fillColor}
      d={
        isVisible
          ? 'M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0-4.5c5 0 9.27 3.11 11 7.5-1.73 4.39-6 7.5-11 7.5S2.73 16.39 1 12c1.73-4.39 6-7.5 11-7.5ZM3.18 12a9.822 9.822 0 0 0 17.64 0 9.821 9.821 0 0 0-17.64 0Z'
          : 'M2 5.27 3.28 4 20 20.72 18.73 22l-3.08-3.08c-1.15.38-2.37.58-3.65.58-5 0-9.27-3.11-11-7.5.69-1.76 1.79-3.31 3.19-4.54L2 5.27ZM12 9a3 3 0 0 1 2.83 4L11 9.17A3 3 0 0 1 12 9Zm0-4.5c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-4 5.19l-1.42-1.43A9.86 9.86 0 0 0 20.82 12 9.82 9.82 0 0 0 12 6.5c-1.09 0-2.16.18-3.16.5L7.3 5.47c1.44-.62 3.03-.97 4.7-.97ZM3.18 12A9.82 9.82 0 0 0 12 17.5c.69 0 1.37-.07 2-.21L11.72 15A3.064 3.064 0 0 1 9 12.28L5.6 8.87c-.99.85-1.82 1.91-2.42 3.13Z'
      }
    />
  </Icon>
);

const ThumbsUp = ({
  width = '19',
  height = '17',
  fillColor = grey70,
  ...rest
}: IconProps) => (
  <Icon
    width={width}
    height={height}
    fill={fillColor}
    viewBox="0 0 19 17"
    {...rest}>
    <path
      fill={fillColor}
      d="M4.167 16.667V5.833L10 0l1.542 1.542-1.084 4.291h7.875l-4.708 10.834zm-4.167 0V5.833h2.5v10.834z"
    />
  </Icon>
);

const ThumbsDown = ({
  width = '19',
  height = '17',
  fillColor = grey70,
  ...rest
}: IconProps) => (
  <Icon
    width={width}
    height={height}
    fill={fillColor}
    viewBox="0 0 19 17"
    {...rest}>
    <path
      fill={fillColor}
      d="M14.167 0v10.833l-5.834 5.834-1.541-1.542 1.083-4.292H0L4.708 0zm4.166 0v10.833h-2.5V0z"
    />
  </Icon>
);

export {
  DocumentIcon,
  UserIcon,
  StoreIcon,
  PhoneIcon,
  EmailIcon,
  VanIcon,
  CarIcon,
  TrainIcon,
  CalendarIcon,
  PersonIcon,
  TicketIcon,
  LocationPinIcon,
  LocationPinOutlinedIcon,
  PriceDrop,
  Clearance,
  RedemptionOffer,
  PasswordVisibilityIcon,
  ThumbsUp,
  ThumbsDown,
};
